import React, { useState, useEffect } from "react";
import { useNotification } from "../../context/NotificationContext";

const UniversityForm = ({ university, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    nameEn: "",
    nameLocal: "",
    mailPattern: "",
    status: true,
  });
  const [errors, setErrors] = useState({});
  const { showError } = useNotification();

  useEffect(() => {
    if (university) {
      setFormData({
        nameEn: university.nameEn || "",
        nameLocal: university.nameLocal || "",
        mailPattern: university.mailPattern || "",
        status: university.status ?? true,
      });
    } else {
      setFormData({
        nameEn: "",
        nameLocal: "",
        mailPattern: "",
        status: true,
      });
    }
    setErrors({});
  }, [university]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nameEn.trim()) {
      newErrors.nameEn = "English name is required";
    }

    if (!formData.nameLocal.trim()) {
      newErrors.nameLocal = "Local name is required";
    }

    if (!formData.mailPattern.trim()) {
      newErrors.mailPattern = "Mail pattern is required";
    } else {
      // Basic email pattern validation
      const domainPattern = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!domainPattern.test(formData.mailPattern)) {
        newErrors.mailPattern =
          "Please enter a valid email domain pattern (e.g., metu.edu.tr, harvard.edu)";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showError("Please fix the validation errors");
      return;
    }

    onSubmit(formData);
  };

  return (
    <div className="form-modern">
      <form onSubmit={handleSubmit} noValidate>
        <div className="form-section">
          <div className="form-section-header">
            <i className="fas fa-university"></i>
            <h6>University Information</h6>
          </div>

          <div className="row">
            {/* English Name */}
            <div className="col-md-6 mb-3">
              <label htmlFor="nameEn" className="form-label">
                University Name (English){" "}
                <span style={{ color: "var(--danger-color)" }}>*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.nameEn ? "is-invalid" : ""}`}
                id="nameEn"
                name="nameEn"
                value={formData.nameEn}
                onChange={handleInputChange}
                placeholder="Enter university name in English"
                disabled={loading}
              />
              {errors.nameEn && (
                <div className="invalid-feedback">{errors.nameEn}</div>
              )}
            </div>

            {/* Local Name */}
            <div className="col-md-6 mb-3">
              <label htmlFor="nameLocal" className="form-label">
                University Name (Local){" "}
                <span style={{ color: "var(--danger-color)" }}>*</span>
              </label>
              <input
                type="text"
                className={`form-control ${
                  errors.nameLocal ? "is-invalid" : ""
                }`}
                id="nameLocal"
                name="nameLocal"
                value={formData.nameLocal}
                onChange={handleInputChange}
                placeholder="Enter university name in local language"
                disabled={loading}
              />
              {errors.nameLocal && (
                <div className="invalid-feedback">{errors.nameLocal}</div>
              )}
            </div>
          </div>

          {/* Mail Pattern */}
          <div className="mb-3">
            <label htmlFor="mailPattern" className="form-label">
              Email Pattern{" "}
              <span style={{ color: "var(--danger-color)" }}>*</span>
            </label>
            <div className="input-group">
              <span className="input-group-text">student@</span>
              <input
                type="text"
                className={`form-control ${
                  errors.mailPattern ? "is-invalid" : ""
                }`}
                id="mailPattern"
                name="mailPattern"
                value={formData.mailPattern}
                onChange={handleInputChange}
                placeholder="university.edu"
                disabled={loading}
              />
              {errors.mailPattern && (
                <div className="invalid-feedback">{errors.mailPattern}</div>
              )}
            </div>
            <div className="form-text">
              <i className="fas fa-info-circle me-1"></i>
              Enter the email domain pattern for this university (e.g.,
              harvard.edu, mit.edu)
            </div>
          </div>

          {/* Status */}
          <div className="mb-4">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="status"
                name="status"
                checked={formData.status}
                onChange={handleInputChange}
                disabled={loading}
              />
              <label className="form-check-label" htmlFor="status">
                University Status
              </label>
            </div>
            <div className="form-text">
              <i
                className={`fas ${
                  formData.status ? "fa-check-circle" : "fa-times-circle"
                } me-1`}
                style={{
                  color: formData.status
                    ? "var(--success-color)"
                    : "var(--danger-color)",
                }}
              ></i>
              University is currently{" "}
              <strong>{formData.status ? "Active" : "Inactive"}</strong>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="form-section">
          <div className="form-section-header">
            <i className="fas fa-eye"></i>
            <h6>Preview</h6>
          </div>
          <div className="row">
            <div className="col-md-6">
              <small style={{ color: "var(--text-muted)" }}>
                English Display:
              </small>
              <div className="fw-medium">
                {formData.nameEn || "University Name (EN)"}
              </div>
            </div>
            <div className="col-md-6">
              <small style={{ color: "var(--text-muted)" }}>
                Local Display:
              </small>
              <div className="fw-medium">
                {formData.nameLocal || "University Name (Local)"}
              </div>
            </div>
          </div>
          <div className="mt-2">
            <small style={{ color: "var(--text-muted)" }}>
              Student Email Example:
            </small>
            <div
              className="font-monospace p-2 rounded border"
              style={{
                background: "var(--bg-secondary)",
                color: "var(--primary-color)",
              }}
            >
              student@{formData.mailPattern || "university.edu"}
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn-modern btn-modern-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-modern btn-modern-primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                {university ? "Updating..." : "Creating..."}
              </>
            ) : (
              <>
                <i
                  className={`fas ${university ? "fa-save" : "fa-plus"} me-2`}
                ></i>
                {university ? "Update University" : "Create University"}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UniversityForm;
